import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Check, Save, Calendar, Trash2, PalmtreeIcon, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../api';
import AddAvailabilitySlideOver from '../components/AddAvailabilitySlideOver';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const SLOTS_PER_HOUR = [0, 30];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FULL_DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TeacherCalendarPage = () => {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [availSlots, setAvailSlots] = useState([]);
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [availType, setAvailType] = useState('long_term');
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveDate, setLeaveDate] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [leaves, setLeaves] = useState([]);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [modePicked, setModePicked] = useState(false);
  const [teacherName, setTeacherName] = useState('');
  useEffect(() => {
    let cancelled = false;
    api.get('/auth/me').then(r => { if (!cancelled) setTeacherName((r.data?.name || '').split(' ')[0] || 'Your'); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const getWeekDays = useCallback(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const fmtTime = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

  const shiftWeek = (dir) => {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + dir * 7);
      return d;
    });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const weekDays = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        weekDays.push(d);
      }
      const startDate = fmtDate(weekDays[0]);
      const endDate = fmtDate(weekDays[6]);

      const [availRes, classesRes, leavesRes] = await Promise.all([
        api.get('/teacher/availability'),
        api.get(`/teacher/calendar-classes?start_date=${startDate}&end_date=${endDate}`),
        api.get('/teacher/leaves')
      ]);
      setAvailSlots(availRes.data || []);
      setScheduledClasses(classesRes.data || []);
      setLeaves(leavesRes.data || []);
    } catch (e) {
      toast.error('Failed to load calendar data');
    }
    setLoading(false);
  }, [weekStart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Build a lookup map: "date|time" -> class info for the START slot of each class
  // And a set of "date|time" keys that are CONTINUATION slots (should be skipped)
  const { classStartMap, skipSlots } = useMemo(() => {
    const startMap = {};
    const skip = new Set();

    for (const cls of scheduledClasses) {
      const classTime = cls.time || cls.start_time || '';
      const [ch, cm] = classTime.split(':').map(Number);
      const duration = cls.duration || 30;
      const slotsNeeded = Math.ceil(duration / 30);
      const isRecurring = cls.schedule_type === 'recurring' || !!cls.schedule_id;

      // End time calculation
      const startTotalMin = ch * 60 + (cm || 0);
      const endTotalMin = startTotalMin + duration;
      const endH = Math.floor(endTotalMin / 60);
      const endM = endTotalMin % 60;
      const endTimeStr = fmtTime(endH, endM);

      const startKey = `${cls.date}|${classTime}`;
      startMap[startKey] = {
        studentName: cls.student_name || 'Student',
        startTime: classTime,
        endTime: endTimeStr,
        duration,
        slotsNeeded,
        isRecurring,
        status: cls.status,
        label: isRecurring ? 'Weekly' : 'Additional',
      };

      // Mark continuation slots for skipping
      for (let s = 1; s < slotsNeeded; s++) {
        const contMin = startTotalMin + s * 30;
        const contH = Math.floor(contMin / 60);
        const contM = contMin % 60;
        const contKey = `${cls.date}|${fmtTime(contH, contM)}`;
        skip.add(contKey);
      }
    }

    return { classStartMap: startMap, skipSlots: skip };
  }, [scheduledClasses]);

  const getAvailStatus = (dateStr, dayName, timeStr) => {
    const [th, tm] = timeStr.split(':').map(Number);
    const targetMin = th * 60 + tm;

    for (const s of availSlots) {
      const slotStart = s.start_time || '';
      const slotEnd = s.end_time || '';
      if (!slotStart || !slotEnd) continue;
      const [sh, smn] = slotStart.split(':').map(Number);
      const [eh, emn] = slotEnd.split(':').map(Number);
      const slotStartMin = sh * 60 + (smn || 0);
      const slotEndMin = eh * 60 + (emn || 0);

      let matches = false;
      if (s.type === 'long_term' && s.day_of_week === dayName) {
        matches = targetMin >= slotStartMin && targetMin < slotEndMin;
      } else if (s.type !== 'long_term' && s.date === dateStr) {
        matches = targetMin >= slotStartMin && targetMin < slotEndMin;
      }

      if (matches) {
        return { type: 'available', label: s.type === 'long_term' ? 'Recurring' : 'Additional', slotId: s.id };
      }
    }

    return null;
  };

  const handleDeleteAvailSlot = async (slotId) => {
    try {
      await api.delete(`/teacher/availability/${slotId}`);
      toast.success('Availability slot removed');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete slot');
    }
  };

  const toggleSlot = (key) => {
    setSelectedSlots(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (selectedSlots.size === 0) { toast.error('No slots selected'); return; }
    setSaving(true);
    try {
      const slots = [];
      selectedSlots.forEach(key => {
        const [dateStr, timeStr] = key.split('|');
        const [h, m] = timeStr.split(':').map(Number);
        const endH = m === 30 ? h + 1 : h;
        const endM = m === 30 ? 0 : 30;
        const dayIdx = new Date(dateStr + 'T12:00:00').getDay();
        slots.push({
          day_of_week: availType === 'long_term' ? FULL_DAY_NAMES[dayIdx] : '',
          date: availType === 'additional' ? dateStr : '',
          start_time: timeStr,
          end_time: fmtTime(endH, endM)
        });
      });
      await api.post('/teacher/availability', { type: availType, slots });
      toast.success(`${slots.length} slot(s) saved as ${availType === 'long_term' ? 'Recurring' : 'Additional'}`);
      setSelectedSlots(new Set());
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleApplyLeave = async () => {
    if (!leaveDate) { toast.error('Select a date'); return; }
    try {
      const res = await api.post('/teacher/leave', { date: leaveDate, reason: leaveReason || 'Personal leave' });
      toast.success(res.data.message);
      setLeaveDialogOpen(false);
      setLeaveDate('');
      setLeaveReason('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCancelLeave = async (leaveId) => {
    try { await api.delete(`/teacher/leave/${leaveId}`); toast.success('Leave cancelled'); fetchData(); } catch { toast.error('Failed'); }
  };

  const leaveDates = new Set(leaves.map(l => l.date));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" style={{ fontFamily: "'Nunito', sans-serif" }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600" />
      </div>
    );
  }

  const weekDays = getWeekDays();
  const bookedCount = scheduledClasses.filter(c => ['scheduled', 'rescheduled'].includes(c.status)).length;

  return (
    <div className="min-h-screen v2 v2-app" style={{ fontFamily: "'Outfit', 'Inter', 'Nunito', sans-serif" }} data-testid="teacher-calendar-page">
      {/* Header — v2 */}
      <div className="sticky top-0 z-20 v2-glass border-b border-slate-200/70 rounded-none">
        <div className="max-w-[1600px] mx-auto px-6 py-5">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <img src="/assets/logo.png" alt="CodeITT" className="h-10 w-auto object-contain rounded-xl" data-testid="codeitt-logo" />
              <div>
                <h1 className="v2-page-title" data-testid="calendar-title">
                  {teacherName}&apos;s CodeITT Calendar
                </h1>
                <p className="v2-page-subtitle">Plan, manage and view your teaching schedule.</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* Legend */}
              <div className="flex items-center gap-3 text-xs" data-testid="calendar-legend">
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500" /><span className="font-bold text-slate-600">Booked</span></div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-violet-500" /><span className="font-bold text-slate-600">Recurring</span></div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-500" /><span className="font-bold text-slate-600">Additional</span></div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-rose-500" /><span className="font-bold text-slate-600">Leave</span></div>
              </div>

              {selectedSlots.size > 0 && (
                <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 h-10 rounded-xl shadow-lg shadow-emerald-500/25" data-testid="save-slots-btn">
                  <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : `Save ${selectedSlots.size} slot(s)`}
                </Button>
              )}

              <button
                onClick={() => setChooserOpen(true)}
                className="v2-cta"
                data-testid="add-availability-btn"
              >
                <Plus className="w-4 h-4" /> Add Availability
              </button>
            </div>
          </div>
          {modePicked && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className="px-2.5 py-0.5 rounded-full font-bold bg-violet-100 text-violet-700 border border-violet-200">
                Mode: {availType === 'long_term' ? 'Recurring' : 'Additional'} availability
              </span>
              <button onClick={() => { setSelectedSlots(new Set()); setModePicked(false); }} className="text-slate-500 hover:text-slate-900 font-semibold">Clear selection</button>
            </div>
          )}
        </div>
      </div>

      {/* + Add Availability slide-over */}
      <AddAvailabilitySlideOver
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onRecurring={() => { setAvailType('long_term'); setModePicked(true); toast.info('Click empty slots to mark them as recurring availability.'); }}
        onAdditional={() => { setAvailType('additional'); setModePicked(true); toast.info('Click empty slots to add a one-time availability.'); }}
        onLeave={() => { setLeaveDialogOpen(true); }}
      />

      {/* Apply Leave Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent className="bg-white border border-slate-200 shadow-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#0F172A]">Apply Leave</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <label className="text-xs font-bold text-[#334155]">Date</label>
              <Input type="date" value={leaveDate} onChange={e => setLeaveDate(e.target.value)} className="mt-1" data-testid="leave-date-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-[#334155]">Reason (optional)</label>
              <Input value={leaveReason} onChange={e => setLeaveReason(e.target.value)} placeholder="Personal leave" className="mt-1" data-testid="leave-reason-input" />
            </div>
            <p className="text-xs text-[#64748B]">Scheduled classes on this day will be marked for rescheduling.</p>
            <Button onClick={handleApplyLeave} className="w-full bg-rose-500 hover:bg-rose-600 text-white rounded-xl" data-testid="confirm-leave-btn">Apply Leave</Button>
          </div>
          {/* Existing Leaves */}
          {leaves.length > 0 && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <p className="text-xs font-bold text-[#334155] mb-2">Your Leaves</p>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {leaves.slice(0, 10).map(l => (
                  <div key={l.id} className="flex items-center justify-between px-3 py-1.5 bg-rose-50 rounded-lg text-xs">
                    <span className="font-medium text-[#0F172A]">{l.date} — {l.reason}</span>
                    <button onClick={() => handleCancelLeave(l.id)} className="text-rose-500 hover:text-rose-700"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* KPI strip */}
      <div className="max-w-[1600px] mx-auto px-6 pt-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="v2-kpi" data-testid="kpi-available-slots">
            <div className="v2-kpi-label">Available Slots</div>
            <div className="v2-kpi-value">{availSlots.length}</div>
          </div>
          <div className="v2-kpi" data-testid="kpi-classes-week">
            <div className="v2-kpi-label">Classes This Week</div>
            <div className="v2-kpi-value">{bookedCount}</div>
          </div>
          <div className="v2-kpi" data-testid="kpi-teaching-hours">
            <div className="v2-kpi-label">Teaching Hours</div>
            <div className="v2-kpi-value">{Math.round(bookedCount)}<span className="text-slate-400 text-2xl">h</span></div>
          </div>
          <div className="v2-kpi" data-testid="kpi-leaves">
            <div className="v2-kpi-label">Leaves Planned</div>
            <div className="v2-kpi-value">{leaves.length}</div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="max-w-[1600px] mx-auto px-6 py-6">
        <div className="v2-card overflow-hidden">
          {/* Week Navigation */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200/70 bg-slate-50/60">
            <button onClick={() => shiftWeek(-1)} className="p-1.5 hover:bg-white/80 rounded-xl transition-colors" data-testid="prev-week-btn">
              <ChevronLeft className="w-5 h-5 text-slate-700" />
            </button>
            <h2 className="text-lg font-extrabold text-slate-900 tracking-tight" data-testid="week-range">
              {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} &mdash; {weekDays[6].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </h2>
            <button onClick={() => shiftWeek(1)} className="p-1.5 hover:bg-white/80 rounded-xl transition-colors" data-testid="next-week-btn">
              <ChevronRight className="w-5 h-5 text-slate-700" />
            </button>
          </div>

          {/* Scrollable Table */}
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
            <table className="w-full border-collapse" style={{ minWidth: '900px' }}>
              <thead className="sticky top-0 z-10 bg-white">
                <tr>
                  <th className="p-2 text-xs font-bold text-slate-500 border-b border-r border-slate-200 w-16 text-center bg-slate-50">Time</th>
                  {weekDays.map((d, i) => {
                    const isToday = fmtDate(d) === fmtDate(new Date());
                    const isLeaveDay = leaveDates.has(fmtDate(d));
                    return (
                      <th key={i} className={`p-2.5 text-center border-b border-r ${isLeaveDay ? 'bg-rose-50' : isToday ? 'bg-blue-50' : 'bg-white/50'}`} style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                        <div className="text-xs font-bold text-[#64748B] uppercase">{DAY_NAMES[d.getDay()]}</div>
                        <div className={`text-lg font-extrabold ${isLeaveDay ? 'text-rose-500' : isToday ? 'text-blue-600' : 'text-[#0F172A]'}`}>{d.getDate()}</div>
                        <div className="text-[10px] text-[#94A3B8] font-medium">{d.toLocaleDateString('en-US', { month: 'short' })}</div>
                        {isLeaveDay && <div className="text-[9px] font-bold text-rose-500 mt-0.5">ON LEAVE</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {HOURS.map(hour =>
                  SLOTS_PER_HOUR.map(min => {
                    const timeStr = fmtTime(hour, min);
                    const isPM = hour >= 12;
                    const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                    const timeLabel = min === 0 ? `${h12}:00 ${isPM ? 'PM' : 'AM'}` : `${h12}:30`;

                    return (
                      <tr key={`${hour}-${min}`} className={min === 0 ? 'border-t border-slate-200' : ''}>
                        <td className={`px-1 py-0 text-right pr-2 border-r border-slate-200 whitespace-nowrap ${min === 0 ? 'text-xs font-bold text-slate-500' : 'text-[10px] text-slate-400'}`}
                          style={{ height: '36px' }}>
                          {timeLabel}
                        </td>
                        {weekDays.map((d, di) => {
                          const dateStr = fmtDate(d);
                          const dayName = FULL_DAY_NAMES[d.getDay()];
                          const slotKey = `${dateStr}|${timeStr}`;

                          // If this slot is a continuation of a multi-slot class, skip rendering the <td>
                          if (skipSlots.has(slotKey)) {
                            return null;
                          }

                          const classInfo = classStartMap[slotKey];
                          const isSelected = selectedSlots.has(slotKey);
                          const isToday = dateStr === fmtDate(new Date());

                          // Render a booked class (potentially spanning multiple rows)
                          if (classInfo) {
                            const rowSpan = classInfo.slotsNeeded;
                            const is3D = classInfo.isRecurring;
                            return (
                              <td
                                key={di}
                                rowSpan={rowSpan}
                                className="border-r border-b relative p-1"
                                style={{
                                  backgroundColor: is3D ? '#7c3aed' : '#7c3aed',
                                  height: `${rowSpan * 36}px`,
                                  verticalAlign: 'top',
                                  minWidth: '100px',
                                  borderColor: 'rgba(0,0,0,0.06)',
                                }}
                                data-testid={`cal-slot-${dateStr}-${timeStr.replace(':', '')}`}
                              >
                                <div className={`w-full h-full rounded-xl flex flex-col justify-center px-2.5 py-1.5 ${is3D ? 'shadow-lg' : ''}`}
                                  style={{
                                    color: '#ffffff',
                                    ...(is3D ? { background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 50%, #5b21b6 100%)', boxShadow: '0 4px 12px rgba(124,58,237,0.4), inset 0 1px 0 rgba(255,255,255,0.15)' } : {})
                                  }}>
                                  <div className="text-xs font-bold truncate leading-tight">{classInfo.studentName}</div>
                                  <div className="flex items-center gap-1 mt-0.5">
                                    <Calendar className="w-3 h-3 opacity-70 flex-shrink-0" />
                                    <span className="text-[10px] opacity-80">{classInfo.startTime} - {classInfo.endTime}</span>
                                    {classInfo.isRecurring && (
                                      <span className="text-[9px] font-bold px-1.5 rounded-lg ml-auto" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}>
                                        ({classInfo.label})
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            );
                          }

                          // Check availability
                          const availStatus = getAvailStatus(dateStr, dayName, timeStr);
                          if (availStatus) {
                            const isRecurring = availStatus.label === 'Recurring';
                            return (
                              <td
                                key={di}
                                className="border-r border-b relative group/avail"
                                style={{ minWidth: '100px', padding: '2px', height: '36px', borderColor: 'rgba(0,0,0,0.06)' }}
                                data-testid={`cal-slot-${dateStr}-${timeStr.replace(':', '')}`}
                              >
                                <div className={`px-2 py-1 w-full h-full flex items-center justify-between rounded-xl ${isRecurring ? 'bg-blue-100 border border-blue-300' : 'bg-orange-400 border border-orange-500'}`}>
                                  <span className={`text-[10px] font-bold ${isRecurring ? 'text-blue-700' : 'text-white'}`}>Available</span>
                                  <div className="flex items-center gap-1">
                                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-lg ${isRecurring ? 'bg-blue-200 text-blue-700' : 'bg-orange-200 text-orange-700'}`}>{availStatus.label}</span>
                                    <button onClick={() => handleDeleteAvailSlot(availStatus.slotId)}
                                      className={`opacity-0 group-hover/avail:opacity-100 transition-opacity p-0.5 rounded-lg ${isRecurring ? 'hover:bg-blue-200' : 'hover:bg-orange-200'}`}
                                      title="Remove availability">
                                      <Trash2 className={`w-3 h-3 ${isRecurring ? 'text-blue-600' : 'text-orange-600'}`} />
                                    </button>
                                  </div>
                                </div>
                              </td>
                            );
                          }

                          // Selected slot
                          if (isSelected) {
                            return (
                              <td
                                key={di}
                                onClick={() => toggleSlot(slotKey)}
                                className="border-r border-b cursor-pointer"
                                style={{ minWidth: '100px', padding: '2px', height: '36px', borderColor: 'rgba(0,0,0,0.06)' }}
                                data-testid={`cal-slot-${dateStr}-${timeStr.replace(':', '')}`}
                              >
                                <div className="w-full h-full flex items-center justify-center bg-emerald-100 border-2 border-emerald-500 rounded-xl shadow-sm">
                                  <Check className="w-4 h-4 text-emerald-600" />
                                </div>
                              </td>
                            );
                          }

                          // Empty slot
                          const isLeaveDay = leaveDates.has(dateStr);
                          return (
                            <td
                              key={di}
                              onClick={() => { if (!isLeaveDay) toggleSlot(slotKey); }}
                              className={`border-r border-b relative group ${isLeaveDay ? 'bg-rose-50/40 cursor-not-allowed' : isToday ? 'bg-blue-50/20 cursor-pointer hover:bg-amber-50/50' : 'bg-white/40 cursor-pointer hover:bg-amber-50/50'} transition-colors`}
                              style={{ minWidth: '100px', padding: 0, height: '36px', borderColor: 'rgba(0,0,0,0.06)' }}
                              data-testid={`cal-slot-${dateStr}-${timeStr.replace(':', '')}`}
                            >
                              {!isLeaveDay && (
                                <div className="w-full h-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="w-3 h-3 rounded border border-amber-300" />
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherCalendarPage;
